import {Component, EventEmitter, input, Input, OnChanges, Output, inject, DestroyRef} from '@angular/core';
import {Rota} from '../../../classes/models/rota/Rota';
import {Employee} from '../../../classes/models/employee/Employee';
import {PredictionRotaWeek} from '../../../classes/models/PredictionRotaWeek';
import {Router} from '@angular/router';
import {EmployeeService} from '../../../services/http/employee/employee.service';
import {forkJoin} from 'rxjs';
import {RotaDataService} from '../../../services/store/rota-data.service';
import {DataService} from '../../../services/store/data.service';
import {EmployeesSortService} from '../../../services/employee/employees-sort.service';
import {EmployeeCapacity} from '../../../classes/models/employee/EmployeeCapacity';
import {asIsoWeekday, DayOfWeek, dayOfWeekValues} from '../../../classes/enums/DayOfWeek';
import moment from 'moment';
import {
  RequiredEmployeesPredictionService
} from '../../../services/http/prediction/required-employees-prediction.service';
import {RotaService} from '../../../services/http/rota/rota-service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from '../../../shared/standalone-imports';
import {RotaWeekSelectComponent} from "./rota-week-select/rota-week-select.component";
import {RotaWeekWorkDayComponent} from "./rota-week-work-day/rota-week-work-day.component";
import {RotaWeekHolidayComponent} from "./rota-week-holiday/rota-week-holiday.component";

/**
 * This component displays the data of the weekly rota.
 */
@Component({
  selector: 'app-rota-week-data',
  templateUrl: './rota-week-data.component.html',
  styleUrls: ['./rota-week-data.component.less'],
  imports: [SHARED_IMPORTS, RotaWeekSelectComponent, RotaWeekWorkDayComponent, RotaWeekHolidayComponent]
})
export class RotaWeekDataComponent implements OnChanges {
  private router = inject(Router);
  private employeeService = inject(EmployeeService);
  private rotaService = inject(RotaService);
  private requiredEmployeesPredictionService = inject(RequiredEmployeesPredictionService);
  private rotaDataService = inject(RotaDataService);
  private employeesSortService = inject(EmployeesSortService);
  dataService = inject(DataService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** An identifier of a week, for which a rota should be displayed. */
  weekId = input<string>();
  /** `True` if links to other pages are enabled. */
  showLinks = input<boolean>();
  /** An event to inform a parent component that all rota data is retrieved and calculated. */
  @Output() isLoaded: EventEmitter<boolean> = new EventEmitter();

  rota: Rota;
  /** Selected week of predictions and rotas. */
  selectedWeek: PredictionRotaWeek;
  /** All employees of the selected store. */
  employees: Employee[] = [];
  /** A map of an employee identifier to his working hours. */
  workingHoursByEmployee = new Map<string, number>();
  /** A map, which shows how many hours was worked on each day of week. */
  workingHoursByDay = new Map<DayOfWeek, number>();
  /** A total number of hours, worked in a week. */
  totalWorkingHours: number;
  /** The difference between total actual working hours and total contract working hours. */
  totalWorkingHoursVsContract: number;
  /** A map of day of week to employee's capacity for each hour. */
  employeeCapacityByDayOfWeek = new Map<DayOfWeek, EmployeeCapacity[]>();
  /** A map of employee to the number of his contract hours. */
  contractHoursByEmployee = new Map<string, number>();
  /** A map from a manager as an employee to a flag, indicating if they can do sales. */
  canDoSalesByEmployee: Map<Employee, boolean>;
  /** `True` if a request to backend is in progress. */
  loading = false;
  /** Each day of the week. */
  weekdays: DayOfWeek[] = dayOfWeekValues();

  /**
   * Clears all data. Then retrieves some data from backend, re-calculate other data.
   */
  ngOnChanges(): void {
    this.rotaDataService.clearAllData();
    this.updateAllData();
    this.selectedWeek = this.getRotaWeek(this.weekId());
    const selectedStore = this.dataService.selectedStore;
    if (this.selectedWeek && this.selectedWeek.rotaExist && selectedStore) {
      this.getData(selectedStore.slug, this.selectedWeek);
    } else {
      this.router.navigateByUrl('/');
    }
  }

  private getData(storeId: string, currentWeek: PredictionRotaWeek): void {
    let requiredEmployeesPredictions;
    this.loading = true;
    this.isLoaded.emit(false);
    forkJoin([
      this.rotaService.getRota(storeId, currentWeek.from),
      this.employeeService.get(storeId),
      this.requiredEmployeesPredictionService.getPredictions(storeId, currentWeek.from, currentWeek.to)
    ])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: ([_rota, _employees, _requiredEmployeesPredictions]) => {
                this.rota = _rota;
                this.employees = this.employeesSortService.sortByRole(_employees);
                requiredEmployeesPredictions = _requiredEmployeesPredictions;
              },
              error: () => {
                this.router.navigateByUrl('/');
              },
              complete: () => {
                this.rotaDataService.calculateAndSaveData(
                        currentWeek,
                        this.rota,
                        this.employees,
                        requiredEmployeesPredictions
                );
                this.updateAllData();
                this.getTotals();
                this.loading = false;
                this.isLoaded.emit(true);
              }
            });
  }

  private updateAllData(): void {
    this.workingHoursByDay = this.rotaDataService.workingHoursByDay();
    this.workingHoursByEmployee = this.rotaDataService.workingHoursByEmployee();
    this.contractHoursByEmployee = this.rotaDataService.contractHoursByEmployee();
    this.employeeCapacityByDayOfWeek = this.rotaDataService.employeeCapacityByDay();
    this.canDoSalesByEmployee = this.rotaDataService.canDoSalesByEmployee();
  }

  /**
   * Checks if there is any assignment in a rota for date and employee when an employee must work.
   * @param dayOfWeek the date of week of an assignment.
   * @param employee the employee, who has been assigned.
   */
  anyWorkAssignmentExists(dayOfWeek: DayOfWeek, employee: Employee): boolean {
    const rotaAssignmentsExist = this.rota.rotaAssignments
            .some(assignment => assignment.hasEqualEmployeeAndDay(employee.employeePositionId, dayOfWeek));
    const roleAssignmentsExist = this.rota.roleAssignments
            .some(assignment => assignment.hasEqualEmployeeAndDay(employee.employeePositionId, dayOfWeek));
    return rotaAssignmentsExist || roleAssignmentsExist;
  }

  private getTotals(): void {
    this.totalWorkingHours = Array.from(this.workingHoursByDay.values())
            .reduce((previous, current) => previous + current, 0);
    const totalContractedHours = Array.from(this.contractHoursByEmployee.values())
            .reduce((previous, current) => previous + current, 0);
    this.totalWorkingHoursVsContract = this.totalWorkingHours - totalContractedHours;
  }

  /**
   * Finds the difference between total working hours by an employee and his contract hours.
   * @param employee an employee, which hours difference is displayed.
   */
  getEmployeeWorkingHoursVsContract(employee: Employee): number {
    const workingHours = this.workingHoursByEmployee.get(employee.employeePositionId.value);
    const contractHours = this.contractHoursByEmployee.get(employee.employeePositionId.value);
    if (!workingHours || !contractHours) {
      return 0;
    }
    return workingHours - contractHours;
  }

  /**
   * Checks if there are fewer assigned employees than predicted for the given date.
   * @param dayOfWeek the day of week, which might be understaffed.
   */
  isUnderStaffed(dayOfWeek: DayOfWeek): boolean {
    if (!this.employeeCapacityByDayOfWeek.has(dayOfWeek)) {
      return false;
    }
    return this.employeeCapacityByDayOfWeek.get(dayOfWeek)
            .filter(employeeCapacity => employeeCapacity.isUndercapacity())
            .length > 0;
  }

  /**
   * Checks if there are more assigned employees than predicted for the given date.
   * @param dayOfWeek the day of week, which might be overstaffed.
   */
  isOverStaffed(dayOfWeek: DayOfWeek): boolean {
    if (!this.employeeCapacityByDayOfWeek.has(dayOfWeek)) {
      return false;
    }
    if (this.isUnderStaffed(dayOfWeek)) {
      return false;
    }
    return this.employeeCapacityByDayOfWeek.get(dayOfWeek)
            .filter(employeeCapacity => employeeCapacity.isOvercapacity())
            .length > 0;
  }

  /**
   * Goes to the given date's rota page.
   * @param dayOfWeek the day of week of a rota.
   */
  navigateToDay(dayOfWeek: DayOfWeek) {
    if (this.showLinks) {
      this.router.navigate(['rota-day', this.convertToDate(dayOfWeek)]);
    }
  }

  /**
   * Converts day of week to a date in the selected week.
   * @param dayOfWeek day of week, which will be converted to a date.
   */
  convertToDate(dayOfWeek: DayOfWeek): string {
    const isoWeekday = asIsoWeekday(dayOfWeek);
    return moment(this.selectedWeek.from).isoWeekday(isoWeekday).format('YYYY-MM-DD');
  }

  private getRotaWeek(id): PredictionRotaWeek {
    if (!this.dataService.predictionRotaWeeks) {
      return;
    }
    return this.dataService.predictionRotaWeeks
            .filter(week => week.rotaExist)
            .find(week => week.id === id);
  }
}
